import React, { useEffect, useState } from "react";

import { useNavigate } from "react-router-dom";

// Keeping track of menu expansion
import { useSelector, useDispatch } from "react-redux";
import {
  setMenuOpen,
  // setActivePage,
  // selectActivePage,
  setActiveMenu,
  selectActiveMenu,
  selectMenuOpen,
} from "../../redux/features/applicationState/applicationStateSlice";

// Handling user logout
import { logoutUser } from "../../redux/features/userState/userStateSlice";
import { resetApplication } from "../../redux/features/applicationState/applicationStateSlice";
import { resetPatient } from "../../redux/features/patientState/patientStateSlice";

// Custom components
import toastManager from "../../helpers/toastManager";

// Icons
import { ReactComponent as DermiLogoWhiteIcon } from "./Icons/dermi_logo_white.svg";

import {
  HomeIcon,
  CalculatorIcon,
  UsersIcon,
  UserIcon,
  WrenchScrewdriverIcon,
  QuestionMarkCircleIcon,
  ArrowLeftOnRectangleIcon,
  CloudIcon,
  ServerStackIcon,
} from "@heroicons/react/24/outline";

import HomeSlideOutPanel from "./slideOutPanels/HomeSlideOutPanel";
import RapidSlideOutPanel from "./slideOutPanels/RapidSlideOutPanel";
import PatientsSlideOutPanel from "./slideOutPanels/PatientsSlideOutPanel";
import SettingsSlideOutPanel from "./slideOutPanels/SettingsSlideOutPanel";
import ProfileSlideOutPanel from "./slideOutPanels/ProfileSlideOutPanel";
import HelpSlideOutPanel from "./slideOutPanels/HelpSlideOutPanel";

const ServerTypeIndicator = () => {
  const DEFAULT_ADDRESS = process.env.REACT_APP_BASE_URL;

  const serverConfiguration =
    DEFAULT_ADDRESS === "https://server.dermi.ai/dashboard-api/"
      ? "CLOUD"
      : "LOCAL";

  const ServerIcon =
    serverConfiguration === "CLOUD" ? CloudIcon : ServerStackIcon;

  return (
    <div className="my-3 p-2 rounded-lg Xbg-white relative">
      <div className="relative h-8 w-8">
        <ServerIcon className="h-full w-full text-neutral-400" />
        <div className="absolute bottom-0 right-0 h-2 w-2 rounded-full bg-emerald-400 border border-1 border-white" />
      </div>
    </div>
  );
};

const SidebarItem = ({ Icon, activeMenu, menuKey, actionFunction }) => {
  const dispatch = useDispatch();

  const handleMenuSelection = () => {
    dispatch(setActiveMenu(menuKey));
    dispatch(setMenuOpen(true));
  };

  return (
    <div
      className={`my-3 p-3 rounded-lg hover:bg-black hover:invert cursor-pointer duration-150 Xbg-red-500 
      active:outline active:outline-2 active:outline-cyan-800
    ${activeMenu === menuKey ? "outline outline-1 outline-white" : ""}
    `}
      onClick={actionFunction ? actionFunction : handleMenuSelection}
    >
      <Icon className="text-white w-7 h-7 Xbg-blue-500" />
    </div>
  );
};

const SlideOutPanel = ({ panelOpen, activeMenu }) => {
  const slideOutPanels = {
    home: <HomeSlideOutPanel />,
    calculate: <RapidSlideOutPanel />,
    patients: <PatientsSlideOutPanel />,
    user: <ProfileSlideOutPanel />,
    settings: <SettingsSlideOutPanel />,
    help: <HelpSlideOutPanel />,
  };
  const slideOutPanelTitles = {
    home: "Home",
    calculate: "Rapid Tools",
    patients: "Patient Menu",
    user: "Profile Options",
    settings: "Settings",
    help: "Help",
  };

  const [panelWiperOpen, setPanelWiperOpen] = useState(true);
  const [slideOutPanelActiveMenu, setSlideOutPanelActiveMenu] =
    useState("home");
  const [activePanelTitle, setActivePanelTitle] = useState("Home");
  const [activePanel, setActivePanel] = useState(<HomeSlideOutPanel />);

  useEffect(() => {
    if (activeMenu !== slideOutPanelActiveMenu) {
      setPanelWiperOpen(false);

      setTimeout(() => {
        setSlideOutPanelActiveMenu(activeMenu);
        setActivePanelTitle(slideOutPanelTitles[activeMenu]);
        setActivePanel(slideOutPanels[activeMenu]);

        // Reopen the wiper
        setPanelWiperOpen(true);
      }, 100);
    }
  }, [activeMenu]);

  return (
    <div
      className={`flex flex-col h-full rounded-3xl bg-white shadow-lg transition-all duration-200 overflow-hidden select-none
    ${panelOpen ? "w-[21rem] md:w-[25rem]" : "w-[5rem]"}
    `}
    >
      <div
        className={`${
          panelWiperOpen ? "w-full" : "w-0"
        } h-full overflow-hidden transition-all duration-50 Xbg-red-500`}
      >
        <div
          className={
            "flex flex-col items-start justify-start h-full w-[16rem] md:w-[19rem] ml-[5rem] md:ml-[6rem] p-2 Xbg-red-200"
          }
        >
          {/* Header with active panel title */}
          <div className="flex flex-row items-center justify-center h-10 w-full mt-4 Xbg-orange-500">
            <div className="flex flex-[3] items-center justify-start h-full w-full pl-2 Xbg-pink-500">
              <p className="text-2xl md:text-3xl font-medium text-neutral-800">
                {activePanelTitle}
              </p>
            </div>
            <div className="flex flex-[1] items-center justify-start h-full w-full Xbg-blue-500"></div>
          </div>

          {/* Gradient transition */}
          <div className="relative w-full z-[999]">
            <div className="absolute w-full h-5 bg-gradient-to-b from-0% from-white"></div>
          </div>

          {/* Slide out active panel */}
          <>{activePanel}</>
        </div>
      </div>
    </div>
  );
};

const Sidebar = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const ENTRY_MODULES = JSON.parse(process.env.REACT_APP_ENTRY_MODULES || "[]");

  const activeMenu = useSelector(selectActiveMenu);
  const menuOpen = useSelector(selectMenuOpen);

  const handleMenuOpenToggle = () => {
    dispatch(setMenuOpen(!menuOpen));
  };

  return (
    <>
      {/* Primary bar container */}
      <div
        className={`top-0 bottom-0 z-[998] fixed
                    flex flex-col items-center justify-center h-screen min-h-screen Xp-2 Xbg-pink-500
                    transition-all duration-200
                    ${menuOpen ? "left-0" : "-left-[5rem] md:left-0"}
                   `}
      >
        {/* Primary bar */}
        <div
          className={`flex flex-[3] flex-col h-full w-[5rem] md:w-[6rem] items-center justify-start bg-black pt-2 Xbg-orange-500
                      transition-all duration-200
                      ${menuOpen ? "rounded-tr-xl" : "rounded-tr-3xl"}
                     `}
        >
          <div className="my-4 mb-8 Xbg-red-500" onClick={handleMenuOpenToggle}>
            <DermiLogoWhiteIcon className="bg-whiteX w-10 h-10 cursor-pointer transition-all duration-150 hover:scale-110" />
          </div>

          <SidebarItem
            Icon={HomeIcon}
            activeMenu={activeMenu}
            menuKey={"home"}
          />
          <SidebarItem
            Icon={CalculatorIcon}
            activeMenu={activeMenu}
            menuKey={"calculate"}
          />
          <SidebarItem
            Icon={UsersIcon}
            activeMenu={activeMenu}
            menuKey={"patients"}
          />
        </div>
        <div
          className={`flex flex-[1] flex-col h-full w-[5rem] md:w-[6rem] items-center justify-end bg-black pb-2 Xbg-purple-500
                      transition-all duration-200
                      ${menuOpen ? "rounded-br-xl" : "rounded-br-3xl"}
                     `}
        >
          <SidebarItem
            Icon={UserIcon}
            activeMenu={activeMenu}
            menuKey={"user"}
          />

          {(ENTRY_MODULES.includes("psoriasis") ||
            ENTRY_MODULES.includes("ai_assisted")) && (
            <SidebarItem
              Icon={WrenchScrewdriverIcon}
              activeMenu={activeMenu}
              menuKey={"settings"}
            />
          )}

          <SidebarItem
            Icon={QuestionMarkCircleIcon}
            activeMenu={activeMenu}
            menuKey={"help"}
          />

          <SidebarItem
            Icon={ArrowLeftOnRectangleIcon}
            activeMenu={activeMenu}
            menuKey={"logout"}
            actionFunction={() => {
              navigate("");
              // dispatch(setAppOpen(false));
              dispatch(resetApplication());
              dispatch(logoutUser());
              dispatch(resetPatient());

              toastManager("success", "Logged out!", "See you next time.");
            }}
          />
          <ServerTypeIndicator />
        </div>
      </div>

      {/* Slide out panel container */}
      <div
        className={`top-0 z-[997] fixed flex flex-col items-start justify-start h-full Xp-2 Xbg-orange-500
                    transition-all duration-200
                    ${menuOpen ? "left-0" : "-left-[5rem] md:left-0"}   
                   `}
      >
        {/* Slider out panel */}
        <SlideOutPanel panelOpen={menuOpen} activeMenu={activeMenu} />
      </div>
    </>
  );
};

export default Sidebar;
